/**
 * The call's one AudioContext. Browsers only let audio start from a user gesture, so `unlockAudio()` runs
 * synchronously inside the call-button tap: it creates / resumes the context and plays one silent frame.
 * Everything the call plays (streamed speech, earcons) goes through this context afterwards.
 *
 * Also: the earcons (connect, your-turn tick, end, hold), built from oscillators so there are no assets,
 * and the dBFS → 0..1 meter the orb and pill use.
 */

let ctx: AudioContext | null = null

type AudioCtor = typeof AudioContext

function ctor(): AudioCtor | null {
    if (typeof window === 'undefined') return null
    return window.AudioContext ?? (window as unknown as { webkitAudioContext?: AudioCtor }).webkitAudioContext ?? null
}

export function audioContext(): AudioContext | null {
    return ctx
}

/** Call synchronously from the tap that starts a call. Safe to call again. */
export function unlockAudio(): AudioContext | null {
    try {
        const Ctor = ctor()
        if (!Ctor) return null
        if (!ctx || ctx.state === 'closed') ctx = new Ctor({ latencyHint: 'interactive' })
        if (ctx.state !== 'running') void ctx.resume().catch(() => {})
        const silent = ctx.createBuffer(1, 1, ctx.sampleRate)
        const src = ctx.createBufferSource()
        src.buffer = silent
        src.connect(ctx.destination)
        src.start(0)
        // Safari: a call plays and records; keep page audio on the same session as the mic.
        const session = (navigator as unknown as { audioSession?: { type: string } }).audioSession
        if (session && session.type !== 'play-and-record') {
            try {
                session.type = 'play-and-record'
            } catch {
                /* not settable here */
            }
        }
    } catch {
        /* no Web Audio: speech falls back to device TTS */
    }
    return ctx
}

/** dBFS → 0..1 for meters (-60 dB is silence, -10 dB is loud speech). */
export function dbToLevel(db: number): number {
    return Math.max(0, Math.min(1, (db + 60) / 50))
}

/** One-pole smoothing with separate attack and release (ms), for levels read every frame. */
export function smoothLevel(prev: number, target: number, dtMs: number, attackMs = 40, releaseMs = 180): number {
    const tau = target > prev ? attackMs : releaseMs
    const k = 1 - Math.exp(-Math.max(0, dtMs) / tau)
    return prev + (target - prev) * k
}

const EARCONS: Record<string, { f: number; at: number; dur: number }[]> = {
    connect: [{ f: 660, at: 0, dur: 0.11 }, { f: 990, at: 0.1, dur: 0.16 }],
    tick: [{ f: 1320, at: 0, dur: 0.05 }],
    end: [{ f: 740, at: 0, dur: 0.12 }, { f: 494, at: 0.11, dur: 0.2 }],
    hold: [{ f: 520, at: 0, dur: 0.14 }],
}

export function playEarcon(kind: 'connect' | 'tick' | 'end' | 'hold', volume = 0.06): void {
    const c = ctx
    if (!c || c.state !== 'running') return
    try {
        const t0 = c.currentTime + 0.01
        for (const n of EARCONS[kind] ?? []) {
            const osc = c.createOscillator()
            const g = c.createGain()
            osc.type = 'sine'
            osc.frequency.value = n.f
            const peak = kind === 'tick' ? volume * 0.6 : volume
            g.gain.setValueAtTime(0.0001, t0 + n.at)
            g.gain.exponentialRampToValueAtTime(peak, t0 + n.at + 0.012)
            g.gain.exponentialRampToValueAtTime(0.0001, t0 + n.at + n.dur)
            osc.connect(g).connect(c.destination)
            osc.start(t0 + n.at)
            osc.stop(t0 + n.at + n.dur + 0.02)
        }
    } catch {
        /* earcons are a nicety */
    }
}
